/*
 * "Show Original Names": the Case Files tree lists files by the name they
 * were uploaded as instead of their exhibit title. A view setting only:
 * labels, downloads and the packet keep the exhibit titles. Remembered per
 * user, shared across cases and tabs of the same window.
 */
import { useCallback, useSyncExternalStore } from "react";

import { auth } from "@/api/firebaseApi";

export const originalNamesKey = (uid: string | null): string =>
  uid ? `caseIde.originalNames.v1:${uid}` : "caseIde.originalNames.v1";

/** Picks that storage refused (full or disabled), for this tab only. */
const unsaved = new Map<string, boolean>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const readOriginalNames = (uid: string | null): boolean => {
  const key = originalNamesKey(uid);
  const held = unsaved.get(key);
  if (held !== undefined) return held;
  try {
    return window.localStorage.getItem(key) === "on";
  } catch {
    return false;
  }
};

export const writeOriginalNames = (uid: string | null, on: boolean): void => {
  const key = originalNamesKey(uid);
  try {
    if (on) window.localStorage.setItem(key, "on");
    else window.localStorage.removeItem(key);
    unsaved.delete(key);
  } catch {
    unsaved.set(key, on);
  }
  listeners.forEach((listener) => listener());
};

const currentUid = (): string | null => auth.currentUser?.uid ?? null;

/** Whether the signed-in user is viewing original names (off until chosen). */
export const useOriginalNames = (): [boolean, (on: boolean) => void] => {
  const on = useSyncExternalStore(
    subscribe,
    () => readOriginalNames(currentUid()),
    () => false
  );
  const setOn = useCallback(
    (next: boolean) => writeOriginalNames(currentUid(), next),
    []
  );
  return [on, setOn];
};

/** ⌥⇧N on a Mac, Alt+Shift+N elsewhere; by key position, since ⌥ remaps the letter. */
export const isOriginalNamesShortcut = (e: KeyboardEvent): boolean =>
  e.code === "KeyN" && e.altKey && e.shiftKey && !e.metaKey && !e.ctrlKey;
